// components/shared/SlipAttemptTimeline.tsx
// ⭐ Timeline ประวัติการอัพสลิปของ order/deposit order (ใช้ร่วมทั้ง admin + merchant portal)
// self-fetching: โหลดข้อมูลเองจาก fetchUrl (UiModal unmount ลูกตอนปิด → lazy fetch ตอนเปิดอัตโนมัติ)

import React, { useCallback, useEffect, useState } from 'react';
import { merchantApi } from '../../api/config';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faClockRotateLeft, faRotate, faSpinner, faChevronDown, faChevronUp,
} from '@fortawesome/free-solid-svg-icons';
import dayjs from 'dayjs';
import { fmtBaht } from '../../utils/formatMoney';

export interface SlipAttempt {
  id: string;
  order_id?: string | null;
  deposit_order_id?: string | null;
  order_number?: string;
  channel: 'public_page' | 'merchant_api' | 'merchant_portal' | 'admin_portal' | 'telegram' | 'retry_job' | string;
  provider: 'slip2go' | 'easyslip' | 'none' | string;
  input_type?: string;
  actor?: string;
  attempt_no?: number;
  verified: boolean;
  error_stage: string;
  response_code?: string | null;
  error_message?: string | null;
  raw_response?: unknown;
  slip_amount?: number | null;
  created_at: string;
}

// URL helper — admin deposit orders ใช้ adminOrder ได้เลย (endpoint รองรับทั้ง orders.id และ deposit_orders.id)
export const slipAttemptUrls = {
  adminOrder: (id: string) => `/admin/orders/${id}/slip-attempts`,
  merchantOrder: (id: string) => `/merchants/orders/${id}/slip-attempts`,
};

const STAGE_LABEL: Record<string, string> = {
  success: 'ผ่าน',
  forced: 'อนุมัติโดยแอดมิน',
  duplicate: 'สลิปซ้ำ',
  amount_mismatch: 'ยอดเงินไม่ตรง',
  receiver_mismatch: 'บัญชีผู้รับไม่ตรง',
  sender_mismatch: 'บัญชีผู้โอนไม่ตรง',
  provider_error_permanent: 'ตรวจสลิปไม่ผ่าน',
  provider_error_transient: 'รอตรวจซ้ำ (ระบบธนาคารดีเลย์)',
  retry_pending: 'รอรอบตรวจซ้ำ',
  provider_unavailable: 'ระบบตรวจสลิปไม่พร้อม',
  qr_decode_failed: 'อ่าน QR ไม่ได้',
  input_invalid: 'ข้อมูลไม่ถูกต้อง',
  slip_expired: 'สลิปเก่าเกินไป',
  slip_time_invalid: 'เวลาสลิปไม่ถูกต้อง',
  order_state_invalid: 'สถานะรายการไม่ถูกต้อง',
  internal_error: 'ระบบขัดข้อง',
  other: 'อื่นๆ',
};

const CHANNEL_LABEL: Record<string, string> = {
  public_page: 'หน้าชำระเงิน',
  merchant_api: 'Merchant API',
  merchant_portal: 'ร้านค้า (portal)',
  admin_portal: 'แอดมิน',
  telegram: 'Telegram',
  retry_job: 'ระบบ Retry',
};

interface SlipAttemptTimelineProps {
  fetchUrl: string;
  isAdmin?: boolean;
  className?: string;
}

const SlipAttemptTimeline: React.FC<SlipAttemptTimelineProps> = ({ fetchUrl, isAdmin = false, className = '' }) => {
  const [attempts, setAttempts] = useState<SlipAttempt[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [expandedIds, setExpandedIds] = useState<Record<string, boolean>>({});

  const load = useCallback(async () => {
    try {
      setLoading(true);
      setError(false);
      const url = isAdmin
        ? `${fetchUrl}${fetchUrl.includes('?') ? '&' : '?'}include_raw=1`
        : fetchUrl;
      const res = await merchantApi.get(url);
      if (res.data?.success) {
        const data = res.data.data || {};
        setAttempts(data.attempts || []);
        setTotal(data.total ?? (data.attempts || []).length);
      } else {
        setError(true);
      }
    } catch {
      setError(true);
    } finally {
      setLoading(false);
    }
  }, [fetchUrl, isAdmin]);

  useEffect(() => { load(); }, [load]);

  const toggleRaw = (id: string) =>
    setExpandedIds((prev) => ({ ...prev, [id]: !prev[id] }));

  return (
    <div className={`bg-gray-50 dark:bg-slate-700/50 rounded-xl p-3.5 ${className}`}>
      <div className="flex items-center justify-between mb-3">
        <p className="text-xs font-semibold text-gray-600 dark:text-slate-300 flex items-center gap-1.5">
          <FontAwesomeIcon icon={faClockRotateLeft} className="text-blue-500" />
          ประวัติการอัพสลิป
          {!loading && !error && total > 0 && (
            <span className="text-gray-400 dark:text-slate-500 font-normal">({total} ครั้ง)</span>
          )}
        </p>
        <button
          type="button"
          onClick={load}
          disabled={loading}
          title="โหลดใหม่"
          className="w-6 h-6 rounded-md flex items-center justify-center text-gray-400 dark:text-slate-500 hover:text-gray-600 dark:hover:text-slate-300 hover:bg-gray-200 dark:hover:bg-slate-600 transition-colors"
        >
          <FontAwesomeIcon icon={faRotate} className={`text-xs ${loading ? 'animate-spin' : ''}`} />
        </button>
      </div>

      {loading ? (
        <div className="flex items-center gap-2 text-xs text-gray-400 dark:text-slate-500 py-2">
          <FontAwesomeIcon icon={faSpinner} spin />
          <span>กำลังโหลด...</span>
        </div>
      ) : error ? (
        <div className="flex items-center gap-2 text-xs text-red-500 dark:text-red-400 py-2">
          <span>โหลดประวัติการอัพสลิปไม่สำเร็จ</span>
          <button
            type="button"
            onClick={load}
            className="px-2 py-0.5 rounded-md bg-red-50 dark:bg-red-900/30 text-red-600 dark:text-red-400 hover:bg-red-100 dark:hover:bg-red-900/50 font-medium transition-colors"
          >ลองใหม่</button>
        </div>
      ) : attempts.length === 0 ? (
        <p className="text-xs text-gray-400 dark:text-slate-500 py-2">ยังไม่มีประวัติการอัพสลิป</p>
      ) : (
        <div className="border-l-2 border-gray-200 dark:border-slate-600 ml-1.5 pl-4 space-y-3">
          {attempts.map((a) => {
            const stageLabel = STAGE_LABEL[a.error_stage] || STAGE_LABEL.other;
            const hasRaw = isAdmin && a.raw_response != null;
            const expanded = !!expandedIds[a.id];
            return (
              <div key={a.id} className="relative">
                <span
                  className={`absolute -left-[21.5px] top-1 w-2.5 h-2.5 rounded-full ${a.verified ? 'bg-green-500' : 'bg-red-400'}`}
                />
                {/* row 1: เวลา + ผลตรวจ + (admin) channel/provider/response_code */}
                <div className="flex items-center flex-wrap gap-x-2 gap-y-1">
                  <span className="text-xs text-gray-500 dark:text-slate-400 tabular-nums">
                    {dayjs(a.created_at).format('DD/MM/YYYY HH:mm:ss')}
                  </span>
                  <span
                    className={`inline-flex items-center px-1.5 py-0.5 rounded-full text-[10px] font-medium ${
                      a.verified
                        ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400'
                        : 'bg-red-100 text-red-600 dark:bg-red-900/30 dark:text-red-400'
                    }`}
                  >{a.verified ? (a.error_stage === 'forced' ? stageLabel : 'ผ่าน') : stageLabel}</span>
                  {isAdmin && (
                    <>
                      <span className="inline-flex items-center px-1.5 py-0.5 rounded-full text-[10px] font-medium bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400">
                        {CHANNEL_LABEL[a.channel] || a.channel}
                      </span>
                      {a.provider && a.provider !== 'none' && (
                        <span className="text-[10px] text-gray-400 dark:text-slate-500">{a.provider}</span>
                      )}
                      {a.response_code && (
                        <code className="px-1 py-0.5 rounded bg-gray-200 dark:bg-slate-600 text-[10px] text-gray-600 dark:text-slate-300">
                          {a.response_code}
                        </code>
                      )}
                    </>
                  )}
                  {hasRaw && (
                    <button
                      type="button"
                      onClick={() => toggleRaw(a.id)}
                      title={expanded ? 'ซ่อน raw response' : 'ดู raw response'}
                      className="text-gray-400 dark:text-slate-500 hover:text-gray-600 dark:hover:text-slate-300 transition-colors"
                    >
                      <FontAwesomeIcon icon={expanded ? faChevronUp : faChevronDown} className="text-[10px]" />
                    </button>
                  )}
                </div>
                {/* row 2: error message + ยอดสลิป */}
                {(!a.verified && a.error_message) || a.slip_amount != null ? (
                  <div className="mt-0.5 flex items-baseline flex-wrap gap-x-2">
                    {!a.verified && a.error_message && (
                      <span className="text-xs text-red-600 dark:text-red-400">{a.error_message}</span>
                    )}
                    {a.slip_amount != null && (
                      <span className="text-xs text-gray-500 dark:text-slate-400 tabular-nums">{fmtBaht(a.slip_amount)}</span>
                    )}
                  </div>
                ) : null}
                {/* admin: raw response (provider) */}
                {hasRaw && expanded && (
                  <div className="mt-1.5 bg-gray-900 rounded-lg p-3">
                    <pre className="text-xs text-gray-400 overflow-x-auto max-h-36 overflow-y-auto">
                      {typeof a.raw_response === 'string' ? a.raw_response : JSON.stringify(a.raw_response, null, 2)}
                    </pre>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default SlipAttemptTimeline;
